import {
  CSSProperties,
  FC,
  KeyboardEvent,
  Ref,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';

import { useDebounced } from '@/hooks';

import { Input } from '../Input';
import PopoverPanel from '../PopoverPanel';
import { addressAsString, ILocalAddress } from '../molecules/address';
import { useAddressSuggestions, usePlaceDetails } from './hooks';
import { AddressSuggestionsList } from './AddressSuggestionsList';

import styles from './AddressAutocomplete.module.scss';

export const buildAddressFromComponents = (place: google.maps.places.Place) => {
  const address = {} as ILocalAddress;
  const components = place.addressComponents ?? [];

  components.forEach(component => {
    const fields = [
      // Format: [google field, gc field, short or long name]
      // Use this configuration for USA addresses.
      ['street_number', 'address1', 'longText'],
      ['route', 'address1', 'longText'],
      ['locality', 'city', 'longText'],
      ['locality', 'city_code', 'shortText'],
      ['sublocality_level_1', 'city', 'longText'],
      ['sublocality_level_1', 'city_code', 'shortText'],
      ['administrative_area_level_1', 'province', 'longText'],
      ['administrative_area_level_1', 'province_code', 'shortText'],
      ['country', 'country', 'longText'],
      ['country', 'country_code', 'shortText'],
      ['postal_code', 'zip', 'longText'],
    ].filter(rel => rel[0] === component.types[0]);

    fields.forEach(field => {
      address[field[1]] = `${address[field[1]] ? `${address[field[1]]} ` : ''}${
        component[field[2]]
      }`;
    });
  });

  address.latitude = (place?.location as any)?.latitude;
  address.longitude = (place?.location as any)?.longitude;

  return address;
};

type InputElement = HTMLInputElement | HTMLTextAreaElement;

const AddressAutocomplete: FC<IAddressAutocompleteProps> = ({
  className,
  feedback,
  id,
  isInvalid,
  isSmall,
  label,
  name,
  isClearable,
  placeholder,
  onAddressClear,
  onAddressChange,
  ref,
  value,
}) => {
  const [addressValue, setAddressValue] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [showPopoverPanel, setShowPopoverPanel] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [panelStyles, setPanelStyles] = useState<CSSProperties>();

  const debuncedAddressValue = useDebounced(addressValue, 300);
  const reactId = useId();
  const listboxId = `${reactId}-listbox`;
  const statusId = `${reactId}-status`;

  const containerRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);

  const { suggestions, isLoading } = useAddressSuggestions({
    addressQuery: debuncedAddressValue,
  });

  const isPopoverOpen =
    showPopoverPanel && !!addressValue.length && !!suggestions.length;

  const getOptionId = (index: number) => `${listboxId}-option-${index}`;

  const closeSuggestions = () => {
    setShowPopoverPanel(false);
    setActiveIndex(-1);
  };

  const { getPlaceDetails } = usePlaceDetails(placeDetails => {
    const address = buildAddressFromComponents(placeDetails);
    onAddressChange(address);
    setAddressValue(addressAsString(address));
    closeSuggestions();
  });

  const selectSuggestion = (
    suggestion: google.maps.places.AutocompleteSuggestion,
  ) => {
    getPlaceDetails({ placeId: suggestion.placePrediction?.placeId });
  };

  const finalId = id || 'input-address';
  const accessibleLabel = label || placeholder || 'Address';

  useEffect(() => {
    setAddressValue(value ?? '');
  }, [value]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [suggestions]);

  useEffect(() => {
    if (!debuncedAddressValue) {
      setStatusMessage('');
      return;
    }

    if (isLoading) {
      setStatusMessage('Loading address suggestions');
      return;
    }

    if (!suggestions.length) {
      setStatusMessage('No address suggestions available');
      return;
    }

    const count = suggestions.length;
    setStatusMessage(
      `${count} address suggestion${count === 1 ? '' : 's'} available`,
    );
  }, [debuncedAddressValue, isLoading, suggestions.length]);

  useEffect(() => {
    if (activeIndex < 0 || !listboxRef.current) return;

    const activeOption = listboxRef.current.querySelector<HTMLElement>(
      `#${CSS.escape(getOptionId(activeIndex))}`,
    );
    activeOption?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, listboxId]);

  useEffect(() => {
    if (!isPopoverOpen) return;

    const updatePositionStyles = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setPanelStyles({
        top: `${rect.bottom}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
      });
    };

    updatePositionStyles();
    // capture:true so nested overflow scroll (store locator) updates position
    window.addEventListener('scroll', updatePositionStyles, true);
    window.addEventListener('resize', updatePositionStyles);

    return () => {
      window.removeEventListener('scroll', updatePositionStyles, true);
      window.removeEventListener('resize', updatePositionStyles);
    };
  }, [isPopoverOpen]);

  const handleKeyDown = (event: KeyboardEvent<InputElement>) => {
    const { key } = event;
    const lastIndex = suggestions.length - 1;

    if (key === 'Escape') {
      if (!showPopoverPanel && !isPopoverOpen) return;
      event.preventDefault();
      closeSuggestions();
      return;
    }

    if (key === 'ArrowDown') {
      if (!suggestions.length) return;
      event.preventDefault();
      setShowPopoverPanel(true);
      setActiveIndex(current => (current < lastIndex ? current + 1 : 0));
      return;
    }

    if (key === 'ArrowUp') {
      if (!suggestions.length) return;
      event.preventDefault();
      setShowPopoverPanel(true);
      setActiveIndex(current => (current > 0 ? current - 1 : lastIndex));
      return;
    }

    if (key === 'Home') {
      if (!isPopoverOpen) return;
      event.preventDefault();
      setActiveIndex(0);
      return;
    }

    if (key === 'End') {
      if (!isPopoverOpen) return;
      event.preventDefault();
      setActiveIndex(lastIndex);
      return;
    }

    if (key === 'Enter') {
      if (!isPopoverOpen || activeIndex < 0) return;
      event.preventDefault();
      selectSuggestion(suggestions[activeIndex]);
      return;
    }

    if (key === 'Tab') {
      closeSuggestions();
    }
  };

  const handleClear = () => {
    setAddressValue('');
    closeSuggestions();
    setStatusMessage('');
    onAddressClear?.();
  };

  return (
    <div className={styles.address_autocomplete} ref={containerRef}>
      <Input
        aria-activedescendant={
          isPopoverOpen && activeIndex >= 0
            ? getOptionId(activeIndex)
            : undefined
        }
        aria-autocomplete="list"
        aria-controls={listboxId}
        aria-describedby={statusId}
        aria-expanded={isPopoverOpen}
        aria-haspopup="listbox"
        autoComplete="off"
        className={classNames('address', className)}
        feedback={feedback}
        id={finalId}
        isClearable={isClearable}
        isInvalid={isInvalid}
        isSmall={isSmall}
        name={name}
        onChange={event => {
          setAddressValue(event.target.value);
          setShowPopoverPanel(true);
          setActiveIndex(-1);
        }}
        onClear={handleClear}
        onFocus={() => setShowPopoverPanel(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        ref={ref}
        role="combobox"
        value={addressValue}
      >
        {accessibleLabel}
      </Input>
      <div
        aria-atomic="true"
        aria-live="polite"
        className="sr-only"
        id={statusId}
      >
        {statusMessage}
      </div>
      {isPopoverOpen &&
        createPortal(
          <PopoverPanel
            className={styles.suggestions_panel}
            isOpen={isPopoverOpen}
            role="presentation"
            style={panelStyles}
            toggle={() => setShowPopoverPanel(show => !show)}
          >
            <AddressSuggestionsList
              activeIndex={activeIndex}
              getOptionId={getOptionId}
              id={listboxId}
              listRef={listboxRef}
              onActiveIndexChange={setActiveIndex}
              onSuggestionSelected={selectSuggestion}
              suggestions={suggestions}
            />
          </PopoverPanel>,
          document.body,
        )}
    </div>
  );
};

interface IAddressAutocompleteProps {
  className?: string;
  feedback: string;
  id: string;
  isInvalid: boolean;
  label?: string;
  name: string;
  isClearable?: boolean;
  isSmall?: boolean;
  value?: string;
  placeholder?: string;
  onAddressChange: (_arg: any) => void;
  onAddressClear?: () => void;
  ref?: Ref<InputElement | null>;
}

export { AddressAutocomplete };
